import { useState } from "react";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { setWidgetInSheetParams } from "../../../../appRedux/actions/Boards";
import { Button, Divider, Input, Message } from "../../../BP2Components";
import { useIntl } from "react-intl";
import { Table } from "antd";
import "./ListSummariesModal.less";
import { Icon_ChevronDown, Icon_ChevronUp } from "../../../BP2Components/Icons/icons";
import { Field, SummariesValue, Summary, TABLE_FIELDS } from "../types";

const defaultValues: Summary = {
   id: null,
   columnKey: null,
   titleTranslationKey: "",
   valuePrefix: "",
   valueSuffix: "",
   valueDescriptionTranslationKey: "",
   valueLimit: 0,
   valueLimitDescriptionTranslationKey: "",
};

type SummariesSettingsProps = {
   value: SummariesValue;
   opts: Array<{ value: string; label: string }>;
   sumsFromBenefits: Record<string, number>;
};

export const SummariesSettings = ({ value, opts, sumsFromBenefits }: SummariesSettingsProps) => {
   const dispatch = useDispatch();
   const intl = useIntl();

   const [columnSummaries, setColumnSummaries] = useState<Summary[]>(value.columnSummaries || []);
   const [formValues, setFormValues] = useState<Summary>(defaultValues);

   const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
   const [editingRowValues, setEditingRowValues] = useState<Summary | null>(null);

   const [isFormOpen, setIsFormOpen] = useState<boolean>(false);

   const handleFormFieldChange = (
      field: Field,
      val: string | number | { value: string; label: string } | null,
   ) => {
      setFormValues((prev) => ({ ...prev, [field]: val }));
   };

   const normalizeColumnKey = (
      columnKey: string | { value: string; label: string } | null,
   ): string | null => {
      if (columnKey === null) return null;
      if (typeof columnKey === "object") return columnKey.value;
      return columnKey;
   };

   const isValueLimitExceeded = (
      columnKey: string | { value: string; label: string } | null,
      valueLimit: unknown,
   ): boolean => {
      const normalizedKey = normalizeColumnKey(columnKey);
      if (!normalizedKey) return false;

      const currentSum = sumsFromBenefits[normalizedKey] || 0;
      const numericLimit = Number(valueLimit);

      if (isNaN(numericLimit)) return false;

      return currentSum > numericLimit;
   };

   const handleAddNewSummary = () => {
      if (!formValues.columnKey) return;

      if (isValueLimitExceeded(formValues.columnKey, formValues.valueLimit)) {
         return Message.error(intl.formatMessage({ id: "summaries.valueLimitExceededError" }));
      }

      setColumnSummaries((prev) => [
         ...prev,
         {
            ...formValues,
            id: Date.now().toString(),
            columnKey: normalizeColumnKey(formValues.columnKey),
         },
      ]);

      setFormValues(defaultValues);
      setIsFormOpen(false);
   };

   const handleEdit = (record: Summary) => {
      if (!record.id) {
         console.error("Cannot edit record without id");
         return;
      }
      const columnKeyOption = opts.find((o) => o.value === record.columnKey) || null;
      setEditingRowKey(record.id);
      setEditingRowValues({ ...record, columnKey: columnKeyOption });
   };

   const handleEditingRowFieldChange = (
      field: Field,
      val: string | number | { value: string; label: string } | null,
   ) => {
      if (!editingRowValues) return;
      setEditingRowValues((prev) => (prev ? { ...prev, [field]: val } : prev));
   };

   const handleSave = () => {
      if (!editingRowKey || !editingRowValues) return;

      if (isValueLimitExceeded(editingRowValues.columnKey, editingRowValues.valueLimit)) {
         return Message.error(intl.formatMessage({ id: "summaries.valueLimitExceededError" }));
      }

      setColumnSummaries((prev) =>
         prev.map((col) =>
            col.id === editingRowKey
               ? {
                    ...editingRowValues,
                    id: editingRowKey,
                    columnKey: normalizeColumnKey(editingRowValues.columnKey),
                 }
               : col,
         ),
      );

      setEditingRowKey(null);
      setEditingRowValues(null);
   };

   const handleCancel = () => {
      setEditingRowKey(null);
      setEditingRowValues(null);
   };

   const handleDelete = (id: string) => {
      setColumnSummaries((prev) => prev.filter((col) => col.id !== id));
   };

   const handleSaveColumnSummaries = () => {
      dispatch(setWidgetInSheetParams({ ...value, columnSummaries }));
   };

   const tableColumns = [
      {
         title: "",
         key: "actions",
         width: 130,
         render: (_: unknown, record) => {
            const editable = record.id === editingRowKey;
            return (
               <div className="summaries-settings__action-buttons">
                  {editable ? (
                     <>
                        <Button buttonType="primary" onClick={handleSave}>
                           {intl.formatMessage({ id: "global.save" })}
                        </Button>
                        <Button onClick={handleCancel}>
                           {intl.formatMessage({ id: "global.cancel" })}
                        </Button>
                     </>
                  ) : (
                     <>
                        <Button
                           buttonType="primary"
                           onClick={() => handleEdit(record)}
                           disabled={editingRowKey !== null}
                        >
                           {intl.formatMessage({ id: "global.edit" })}
                        </Button>
                        <Button
                           buttonType="danger"
                           onClick={() => handleDelete(record.id)}
                           disabled={editingRowKey !== null}
                        >
                           {intl.formatMessage({ id: "global.delete" })}
                        </Button>
                     </>
                  )}
               </div>
            );
         },
      },
      ...TABLE_FIELDS.map((field) => ({
         title: intl.formatMessage({ id: `summaries.${field}` }),
         dataIndex: field,
         key: field,
         render: (value, record) => {
            const editable = record.id === editingRowKey;

            if (editable) {
               if (field === "columnKey") {
                  return (
                     <Select
                        options={opts}
                        value={editingRowValues?.columnKey || null}
                        onChange={(val) => handleEditingRowFieldChange("columnKey", val)}
                        className="summaries-settings__select"
                     />
                  );
               }

               return (
                  <Input
                     type={field === "valueLimit" ? "number" : "text"}
                     value={String(editingRowValues?.[field] ?? "")}
                     onChange={(e) =>
                        handleEditingRowFieldChange(
                           field,
                           field === "valueLimit" ? Number(e.target.value) : e.target.value,
                        )
                     }
                  />
               );
            }

            if (field === "columnKey") {
               return opts.find((o) => o.value === value)?.label || "-";
            }

            return <span>{value ?? "-"}</span>;
         },
      })),
   ];

   const rowData = columnSummaries.map((summary) => ({ ...summary, key: summary.id }));

   return (
      <div className="summaries-settings">
         {!value.listId ? (
            <h1 className="summaries-settings__no-list-id">
               {intl.formatMessage({ id: "summaries.noListIdAdded" })}
            </h1>
         ) : (
            <>
               <div
                  className="summaries-settings__accordion-header"
                  onClick={() => setIsFormOpen((prev) => !prev)}
               >
                  <span>{intl.formatMessage({ id: "summaries.addNewSummary" })}</span>
                  <span>
                     {isFormOpen ? (
                        <Icon_ChevronUp width={12} height={8} />
                     ) : (
                        <Icon_ChevronDown width={12} height={8} />
                     )}
                  </span>
               </div>
               {isFormOpen && (
                  <form
                     className="summaries-settings__form"
                     onSubmit={(e) => {
                        e.preventDefault();
                        handleAddNewSummary();
                     }}
                  >
                     <div className="summaries-settings__form-row">
                        <label className="summaries-settings__label">
                           {intl.formatMessage({ id: "summaries.column" })}
                           <Select
                              options={opts}
                              value={formValues.columnKey}
                              onChange={(v) => handleFormFieldChange("columnKey", v)}
                              className="summaries-settings__select"
                           />
                        </label>

                        <label className="summaries-settings__label">
                           {intl.formatMessage({ id: "summaries.titleTranslationKey" })}
                           <Input
                              value={formValues.titleTranslationKey}
                              onChange={(e) =>
                                 handleFormFieldChange("titleTranslationKey", e.target.value)
                              }
                           />
                        </label>
                     </div>

                     <div className="summaries-settings__form-row">
                        <label className="summaries-settings__label">
                           {intl.formatMessage({ id: "summaries.valuePrefix" })}
                           <Input
                              value={formValues.valuePrefix}
                              onChange={(e) => handleFormFieldChange("valuePrefix", e.target.value)}
                           />
                        </label>

                        <label className="summaries-settings__label">
                           {intl.formatMessage({ id: "summaries.valueSuffix" })}
                           <Input
                              value={formValues.valueSuffix}
                              onChange={(e) => handleFormFieldChange("valueSuffix", e.target.value)}
                           />
                        </label>

                        <label className="summaries-settings__label">
                           {intl.formatMessage({ id: "summaries.valueLimit" })} (
                           {intl.formatMessage({ id: "summaries.min" })}{" "}
                           {formValues.columnKey
                              ? (sumsFromBenefits[normalizeColumnKey(formValues.columnKey) ?? ""] ??
                                0)
                              : 0}
                           )
                           <Input
                              type="number"
                              value={String(formValues.valueLimit)}
                              onChange={(e) =>
                                 handleFormFieldChange("valueLimit", Number(e.target.value))
                              }
                           />
                        </label>
                     </div>

                     <div className="summaries-settings__form-row">
                        <label className="summaries-settings__label">
                           {intl.formatMessage({
                              id: "summaries.valueDescriptionTranslationKey",
                           })}
                           <Input
                              value={formValues.valueDescriptionTranslationKey}
                              onChange={(e) =>
                                 handleFormFieldChange(
                                    "valueDescriptionTranslationKey",
                                    e.target.value,
                                 )
                              }
                           />
                        </label>

                        <label className="summaries-settings__label">
                           {intl.formatMessage({
                              id: "summaries.valueLimitDescriptionTranslationKey",
                           })}
                           <Input
                              value={formValues.valueLimitDescriptionTranslationKey}
                              onChange={(e) =>
                                 handleFormFieldChange(
                                    "valueLimitDescriptionTranslationKey",
                                    e.target.value,
                                 )
                              }
                           />
                        </label>
                     </div>

                     <Button
                        htmlType="submit"
                        buttonType="primary"
                        disabled={formValues.columnKey === null}
                     >
                        {intl.formatMessage({ id: "bp2.buttonWidget.Add" })}
                     </Button>
                  </form>
               )}
               <Divider className="summaries-settings__divider" />
               <Table columns={tableColumns} dataSource={rowData} pagination={false} />
               <Button
                  buttonType="primary"
                  onClick={handleSaveColumnSummaries}
                  disabled={!!editingRowKey}
                  className="summaries-settings__save-btn"
               >
                  {intl.formatMessage({ id: "global.preview" })}
               </Button>
            </>
         )}
      </div>
   );
};
